import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, Globe, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ApiKey, ApiKeyScope, ApiKeyTab, Capability, CreatedApiKey, Page } from '@simpleacs/shared';
import {
  CAPABILITIES,
  CAPABILITIES_NEVER_ON_A_KEY,
  CAPABILITY_LABELS,
  DEFAULT_API_KEY_TAB,
  ONU_MANAGEMENT_KEY_CAPABILITIES,
  resolveApiKeyTab,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { GatewayCredentialsCard } from './settings/GatewayCredentials';
import { StorageCredentialsCard } from './settings/StorageCredentials';
import { MapProviderCredentialsCard } from './settings/MapProviderCredentials';
import { McpCard } from './settings/McpCard';
import { ApiKeyLimitsCard } from './settings/ApiKeyLimitsCard';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** What each tab is FOR, in the header, so the page says which job you are on. */
const TAB_BLURB: Record<ApiKeyTab, string> = {
  simpleacs:
    'Access into this platform: the keys an integration or an AI assistant authenticates with.',
  finance:
    'Money moving out and in — the payment gateway credentials the billing run pays through.',
  services:
    'What this platform holds on somebody else’s behalf: object storage, and the map provider.',
};

/**
 * Keys, in tabs — because this page always held two unrelated kinds of secret
 * and showed them as one scroll of cards.
 *
 * The split is by BLAST RADIUS, which is what an operator is actually choosing
 * between when they open this page: handing an integration a scoped token is a
 * routine act, and editing the M-Pesa passkey is not, and stacking them made
 * the second look like more of the first.
 *
 * The tab lives in the URL (`/settings/api-keys?tab=finance`) and switching
 * REPLACES the history entry, exactly as the Settings page does it — same
 * reasons: a tab nobody can link to can only be reached by describing it, and
 * Back should mean "the page I came from", not "the tab I just glanced at".
 */
export function ApiKeysPage() {
  const [params, setParams] = useSearchParams();
  const { activeOrg, session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  // An org user always has an org. A superadmin without one still belongs here:
  // platform keys span every organization and are managed from this page.
  const needsOrg = !isSuperadmin && !activeOrg;

  /*
    The same gate the cards themselves carried when they were stacked here, kept
    exactly: nothing becomes visible to anybody who could not already see it,
    and neither tab can be reached as an empty panel by URL.
  */
  const tabs: Array<{ id: ApiKeyTab; label: string }> = [
    { id: 'simpleacs', label: 'SimpleACS keys' },
    ...(isSuperadmin
      ? ([
          { id: 'finance', label: 'Finance keys' },
          { id: 'services', label: 'Service keys' },
        ] as Array<{ id: ApiKeyTab; label: string }>)
      : []),
  ];
  const tab = resolveApiKeyTab(
    params.get('tab'),
    tabs.map((t) => t.id),
  );

  const select = (next: ApiKeyTab) => {
    const nextParams = new URLSearchParams(params);
    // The default tab is the bare address already in the nav and in bookmarks;
    // spelling it out would quietly create a second URL for one page.
    if (next === DEFAULT_API_KEY_TAB) nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="API keys"
        description={
          tab === 'simpleacs' && activeOrg
            ? `Access into ${activeOrg.name} for integrations and assistants.`
            : TAB_BLURB[tab]
        }
      />

      {tabs.length > 1 && (
        <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
          {tabs.map((entry) => (
            <button
              key={entry.id}
              onClick={() => select(entry.id)}
              className={
                tab === entry.id
                  ? 'border-b-2 border-primary px-4 py-2.5 text-sm font-medium text-primary'
                  : 'border-b-2 border-transparent px-4 py-2.5 text-sm text-muted hover:text-ink'
              }
            >
              {entry.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'simpleacs' &&
        (needsOrg ? (
          <Card>
            <EmptyState
              icon={<KeyRound className="size-8" />}
              title="Select an organization"
              description="API keys are org-wide — pick the organization in the top bar first."
            />
          </Card>
        ) : (
          <div className="space-y-5">
            <ApiKeysCard />
            {/*
              Beside the key list, not on a tab of its own: minting the key and
              pasting it into the snippet are one job, and the snippet is
              useless without it.
            */}
            <McpCard />
            {/* Last: the budgets every key above runs under, and what keys have used of them. */}
            <ApiKeyLimitsCard />
          </div>
        ))}

      {tab === 'finance' && <GatewayCredentialsCard />}

      {tab === 'services' && (
        <div className="space-y-5">
          <StorageCredentialsCard />
          <MapProviderCredentialsCard />
        </div>
      )}
    </>
  );
}

/**
 * Org-wide API keys. Not per-user, deliberately: an integration outlives the
 * person who set it up. Open to the world unless locked to addresses.
 */
function ApiKeysCard() {
  const queryClient = useQueryClient();
  const { activeOrg, can, session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  // Without an active org a superadmin can only mint a platform key.
  const platformOnly = isSuperadmin && !activeOrg;
  /*
    The grant list is the MINTER's own capabilities — the same rule the role
    matrix applies to people, and the same one the API enforces at mint. The
    two a key may never carry are filtered here as a courtesy; the server
    refuses them either way.
  */
  const grantable = CAPABILITIES.filter(
    (c) => can(c) && !(CAPABILITIES_NEVER_ON_A_KEY as readonly string[]).includes(c),
  );
  /*
    The one starting list the custom picker offers: what an assistant or an
    integration that manages subscribers' ONUs needs (see the constant's own
    comment for why each). Intersected with `grantable` for the same reason the
    checklist is — the minter cannot hand out what they do not hold — and the
    remainder is SAID rather than silently dropped, because a key that turned
    out to do less than its preset promised would be debugged as a broken
    assistant, not as a short list.
  */
  const presetGrantable = ONU_MANAGEMENT_KEY_CAPABILITIES.filter((c) => grantable.includes(c));
  const presetLeftOut = ONU_MANAGEMENT_KEY_CAPABILITIES.filter((c) => !grantable.includes(c));

  const [showForm, setShowForm] = useState(false);
  /** What the last preset click could not tick, shown beside the chip until the list is touched again. */
  const [presetNote, setPresetNote] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [form, setForm] = useState({
    name: '',
    scope: 'read' as ApiKeyScope,
    capabilities: [] as Capability[],
    ips: '',
    platform: false,
  });
  const [error, setError] = useState<string | null>(null);
  // The key whose full token is being revealed (drives the password prompt), and
  // the revealed/rolled token to show once.
  const [revealing, setRevealing] = useState<ApiKey | null>(null);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealError, setRevealError] = useState<string | null>(null);
  const [shown, setShown] = useState<{ name: string; token: string; rolled: boolean } | null>(null);
  /** The key open for editing, and the fields as they are being changed. */
  const [editing, setEditing] = useState<ApiKey | null>(null);

  const keysQuery = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<Page<ApiKey>>('/api-keys'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedApiKey>('/api-keys', {
        name: form.name.trim(),
        scope: form.scope,
        capabilities: form.scope === 'custom' ? form.capabilities : [],
        platform: platformOnly || form.platform,
        allowedIps: form.ips
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: async (key) => {
      setCreated(key);
      setShowForm(false);
      setForm({ name: '', scope: 'read', capabilities: [], ips: '', platform: false });
      setPresetNote(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not create the key'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch<ApiKey>(`/api-keys/${encodeURIComponent(id)}`, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api-keys/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const reveal = useMutation({
    mutationFn: (input: { id: string; password: string }) =>
      api.post<{ token: string }>(`/api-keys/${encodeURIComponent(input.id)}/reveal`, {
        password: input.password,
      }),
    onSuccess: (r) => {
      setShown({ name: revealing?.name ?? 'key', token: r.token, rolled: false });
      setRevealing(null);
      setRevealPassword('');
      setRevealError(null);
    },
    onError: (err) => setRevealError(err instanceof Error ? err.message : 'Could not reveal'),
  });

  const roll = useMutation({
    mutationFn: (id: string) => api.post<CreatedApiKey>(`/api-keys/${encodeURIComponent(id)}/roll`),
    onSuccess: async (r) => {
      setShown({ name: r.name, token: r.token, rolled: true });
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const keys = keysQuery.data?.items ?? [];
  // Segmented by what a key can reach: platform keys span every organization,
  // organization keys are one tenant's. The old "Reach" column said the same
  // thing per row; the grouping says it once, where it can be read at a glance.
  const platformKeys = keys.filter((k) => k.platform);
  const orgKeys = keys.filter((k) => !k.platform);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <div className="space-y-5">
      {created && <NewKeyPanel createdKey={created} onClose={() => setCreated(null)} />}
      {shown && (
        <TokenPanel
          name={shown.name}
          token={shown.token}
          title={shown.rolled ? `Key rolled: ${shown.name}` : `Key: ${shown.name}`}
          note={
            shown.rolled
              ? 'The old token stopped working — update anything that used it.'
              : 'Handle it like a password. It can be revealed again with your password.'
          }
          onClose={() => setShown(null)}
        />
      )}
      {editing && (
        <EditKeyPanel
          apiKey={editing}
          saving={update.isPending}
          onSave={async (patch) => {
            await update.mutateAsync({ id: editing.id, patch });
            setEditing(null);
          }}
          onReveal={() => {
            setRevealing(editing);
            setRevealPassword('');
            setRevealError(null);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {revealing && (
        <Card title={`Reveal "${revealing.name}"`}>
          <form
            className="space-y-3 p-5"
            onSubmit={(e) => {
              e.preventDefault();
              reveal.mutate({ id: revealing.id, password: revealPassword });
            }}
          >
            <p className="text-sm text-muted">Confirm your password to reveal the full key.</p>
            <Field label="Your password">
              <Input
                type="password"
                value={revealPassword}
                onChange={(e) => setRevealPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" loading={reveal.isPending}>
                Reveal key
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setRevealing(null);
                  setRevealPassword('');
                  setRevealError(null);
                }}
              >
                Cancel
              </Button>
            </div>
            {revealError && <ErrorNotice error={new Error(revealError)} />}
          </form>
        </Card>
      )}

      <Card
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted" />
            SimpleACS API keys
            <span className="text-xs font-normal text-faint">{keys.length}</span>
          </span>
        }
        action={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => setShowForm((v) => !v)}
          >
            New key
          </Button>
        }
      >
        {showForm && (
          <form onSubmit={onSubmit} className="grid gap-4 border-b border-line p-5 md:grid-cols-3">
            <Field label="Name" hint="What this key is for">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Splynx integration"
                required
                autoFocus
              />
            </Field>
            <Field label="Access">
              <div className="flex gap-4 pt-2">
                {(['read', 'readwrite', 'custom'] as ApiKeyScope[]).map((scope) => (
                  <label
                    key={scope}
                    className="flex cursor-pointer items-center gap-2 text-sm text-ink"
                  >
                    <input
                      type="radio"
                      name="key-scope"
                      checked={form.scope === scope}
                      onChange={() => setForm({ ...form, scope })}
                    />
                    {scope === 'read' ? 'Read' : scope === 'readwrite' ? 'Read / write' : 'Custom'}
                  </label>
                ))}
              </div>
            </Field>
            {form.scope === 'custom' && (
              <Field
                label="What this key may do"
                hint="Only what your own role carries. Managing people and organizations is never offered — a credential that can mint credentials escalates out of its own scope."
              >
                <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted">
                  <span>Start from:</span>
                  <button
                    type="button"
                    title={ONU_MANAGEMENT_KEY_CAPABILITIES.join(', ')}
                    onClick={() => {
                      // Ticks exactly the preset — replacing, not adding to,
                      // whatever was ticked — so "start from" means what it
                      // says and two clicks land on the same list.
                      setForm({ ...form, capabilities: [...presetGrantable] });
                      setPresetNote(
                        presetLeftOut.length === 0
                          ? `Ticked the ${presetGrantable.length} an assistant that manages ONUs needs.`
                          : `Ticked ${presetGrantable.length} of ${ONU_MANAGEMENT_KEY_CAPABILITIES.length}. Left out because your own role does not hold them: ${presetLeftOut.join(', ')}.`,
                      );
                    }}
                    className="rounded-full border border-line px-2.5 py-1 text-ink transition-colors hover:border-primary hover:text-primary"
                  >
                    ONU management
                  </button>
                  <span className="text-faint">
                    read the fleet, act on one ONU or CPE at a time, never a bulk run — the key to hand an AI assistant.
                  </span>
                  {presetNote && <span className="basis-full text-muted">{presetNote}</span>}
                </div>
                <div className="grid gap-1.5 pt-2 sm:grid-cols-2">
                  {grantable.map((capability) => (
                    <label
                      key={capability}
                      className="flex cursor-pointer items-center gap-2 text-sm text-ink"
                    >
                      <input
                        type="checkbox"
                        checked={form.capabilities.includes(capability)}
                        onChange={(e) => {
                          // A hand edit after the preset makes the note stale;
                          // the checklist is now the statement of intent.
                          setPresetNote(null);
                          setForm({
                            ...form,
                            capabilities: e.target.checked
                              ? [...form.capabilities, capability]
                              : form.capabilities.filter((c) => c !== capability),
                          });
                        }}
                      />
                      <span>{CAPABILITY_LABELS[capability]}</span>
                      <span className="font-mono text-[11px] text-faint">{capability}</span>
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <Field
              label="Allowed IPs"
              hint="Addresses or CIDRs, comma separated. Empty = open to the world."
            >
              <Input
                value={form.ips}
                onChange={(e) => setForm({ ...form, ips: e.target.value })}
                placeholder="102.215.34.8, 10.0.0.0/24"
                className="font-mono"
              />
            </Field>
            {isSuperadmin && (
              <Field
                label="Reach"
                hint={
                  platformOnly
                    ? 'No organization selected, so this key spans every one.'
                    : 'A platform key works across every organization.'
                }
              >
                <label className="flex cursor-pointer items-center gap-2 pt-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={platformOnly || form.platform}
                    disabled={platformOnly}
                    onChange={(e) => setForm({ ...form, platform: e.target.checked })}
                  />
                  Platform key (all organizations)
                </label>
              </Field>
            )}
            <div className="md:col-span-3 flex gap-2">
              <Button type="submit" variant="primary" size="sm" loading={create.isPending}>
                Create key
              </Button>
              <Button type="button" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
            {error && (
              <div className="md:col-span-3">
                <ErrorNotice error={new Error(error)} />
              </div>
            )}
          </form>
        )}

        {keysQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : keys.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="No API keys"
            description="Create one to let a billing system or integration drive this platform."
          />
        ) : (
          <>
            {platformKeys.length > 0 && (
              <>
                <p className="px-5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-faint">
                  Platform keys — work across every organization
                </p>
                <KeysTable keys={platformKeys} showOrg={false} />
              </>
            )}
            {orgKeys.length > 0 && (
              <>
                <p
                  className={`px-5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-faint ${
                    platformKeys.length > 0 ? 'border-t border-line' : ''
                  }`}
                >
                  Organization keys — scoped to one tenant
                </p>
                <KeysTable keys={orgKeys} showOrg={isSuperadmin} />
              </>
            )}
          </>
        )}
      </Card>

      {/*
        The gateway, storage and map credentials used to stack under this card
        on one scroll. They are tabs now — see `ApiKeysPage`: a payment
        passkey and an integration token are jobs with very different blast
        radii, and stacking them read as one undifferentiated list of "keys".
      */}
    </div>
  );

  function KeysTable({ keys: rows, showOrg }: { keys: ApiKey[]; showOrg: boolean }) {
    return (
          <Table>
            <THead>
              <th className="th">Name</th>
              <th className="th">Key</th>
              {showOrg && <th className="th">Organization</th>}
              <th className="th">Access</th>
              <th className="th">Allowed from</th>
              <th className="th">Last used</th>
              <th className="th">Status</th>
              <th className="th" />
            </THead>
            <TBody>
              {rows.map((key) => (
                <TRow key={key.id}>
                  <td className="td font-medium">{key.name}</td>
                  <td className="td font-mono text-xs text-muted">{key.tokenPrefix}…</td>
                  {showOrg && (
                    <td className="td">
                      <span className="text-xs text-muted">{key.orgName ?? '—'}</span>
                    </td>
                  )}
                  <td className="td">
                    <span title={key.capabilities.join(', ')}>
                    <Pill state
                      tone={
                        key.scope === 'readwrite' ? 'warn' : key.scope === 'custom' ? 'primary' : 'info'
                      }
                    >
                      {key.scope === 'readwrite'
                        ? 'read / write'
                        : key.scope === 'custom'
                          ? `custom · ${key.capabilities.length}`
                          : 'read'}
                    </Pill>
                    </span>
                  </td>
                  <td className="td">
                    {key.allowedIps.length === 0 ? (
                      <span className="flex items-center gap-1.5 text-xs text-muted">
                        <Globe className="size-3.5" />
                        anywhere
                      </span>
                    ) : (
                      <span className="font-mono text-xs text-muted">
                        {key.allowedIps.join(', ')}
                      </span>
                    )}
                  </td>
                  <td className="td text-muted">
                    {key.lastUsedAt ? (
                      <>
                        {formatRelative(key.lastUsedAt)}
                        {key.lastUsedIp && (
                          <span className="ml-1.5 font-mono text-xs text-faint">
                            {key.lastUsedIp}
                          </span>
                        )}
                      </>
                    ) : (
                      'never'
                    )}
                  </td>
                  <td className="td">
                    <Pill state tone={key.status === 'active' ? 'success' : 'neutral'} dot>
                      {key.status}
                    </Pill>
                  </td>
                  <td className="td">
                    <div className="flex justify-end gap-2">
                      {key.retrievable && (
                        <Button
                          size="sm"
                          icon={<Eye className="size-3.5" />}
                          onClick={() => {
                            setRevealing(key);
                            setRevealPassword('');
                            setRevealError(null);
                          }}
                        >
                          Reveal
                        </Button>
                      )}
                      <Button
                        size="sm"
                        icon={<Pencil className="size-3.5" />}
                        onClick={() => {
                          setEditing(key);
                          setRevealing(null);
                          setShown(null);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        loading={roll.isPending && roll.variables === key.id}
                        onClick={() => {
                          if (
                            confirm(
                              `Roll "${key.name}"? A new token replaces it and the current one stops working immediately.`,
                            )
                          ) {
                            roll.mutate(key.id);
                          }
                        }}
                      >
                        Roll
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Trash2 className="size-3.5" />}
                        onClick={() => {
                          if (
                            confirm(`Revoke "${key.name}"? Anything using it stops working immediately.`)
                          ) {
                            remove.mutate(key.id);
                          }
                        }}
                      >
                        Revoke
                      </Button>
                    </div>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
    );
  }
}

/**
 * Everything about a key that can change, in one place.
 *
 * The parts were already here but scattered: the name and access could only be
 * set at creation, the allowlist could not be changed at all, and status was a
 * button in the row while the rest of the key's shape was nowhere. So a key
 * whose integration moved to a new address had to be rolled and re-issued —
 * which means going and updating whatever was using it, for what is really an
 * edit to one field.
 *
 * Revealing stays behind the password prompt rather than being folded in here.
 * Editing a key is routine; printing the secret on screen is not, and the
 * re-prompt is the only thing separating a borrowed session from the token.
 */
function EditKeyPanel({
  apiKey,
  saving,
  onSave,
  onReveal,
  onClose,
}: {
  apiKey: ApiKey;
  saving: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onReveal: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(apiKey.name);
  const [scope, setScope] = useState<ApiKeyScope>(apiKey.scope);
  const [status, setStatus] = useState(apiKey.status);
  const [ips, setIps] = useState((apiKey.allowedIps ?? []).join(', '));

  const allowedIps = ips
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Pencil className="size-4 text-muted" />
          Edit “{apiKey.name}”
          <span className="font-mono text-xs font-normal text-faint">{apiKey.tokenPrefix}…</span>
        </span>
      }
    >
      <form
        className="grid gap-4 p-5 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave({ name: name.trim(), scope, status, allowedIps });
        }}
      >
        <Field label="Name" hint="What this key is for">
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Access">
          <div className="flex gap-4 pt-2">
            {(['read', 'readwrite'] as ApiKeyScope[]).map((s) => (
              <label key={s} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="edit-key-scope"
                  checked={scope === s}
                  onChange={() => setScope(s)}
                />
                {s === 'read' ? 'Read' : 'Read / write'}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Status" hint="Disabled keys are refused without being revoked">
          <div className="flex gap-4 pt-2">
            {(['active', 'disabled'] as const).map((s) => (
              <label key={s} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="edit-key-status"
                  checked={status === s}
                  onChange={() => setStatus(s)}
                />
                {s === 'active' ? 'Active' : 'Disabled'}
              </label>
            ))}
          </div>
        </Field>
        <Field
          label="Available from"
          hint="Addresses or CIDRs, comma separated. Empty = reachable from anywhere."
        >
          <Input
            value={ips}
            onChange={(e) => setIps(e.target.value)}
            placeholder="102.215.34.8, 10.0.0.0/24"
            className="font-mono"
          />
        </Field>
        <div className="md:col-span-3 flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" size="sm" loading={saving}>
            Save changes
          </Button>
          <Button type="button" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {apiKey.retrievable ? (
            <Button
              type="button"
              size="sm"
              icon={<Eye className="size-3.5" />}
              onClick={onReveal}
              className="ml-auto"
            >
              Show the whole key
            </Button>
          ) : (
            <span className="ml-auto text-xs text-faint">
              This key predates retrievable storage — roll it to get one you can read back.
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function NewKeyPanel({ createdKey, onClose }: { createdKey: CreatedApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Card
      title={`Key created: ${createdKey.name}`}
      action={
        <Button size="sm" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-warn">
          This is the only time the key is shown. Store it now — the server keeps only a hash.
        </p>
        <div className="flex gap-2">
          <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-sm text-ink">
            {createdKey.token}
          </code>
          <Button
            icon={<Copy className="size-4" />}
            onClick={async () => {
              await navigator.clipboard.writeText(createdKey.token);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="font-mono text-xs text-faint">
          Authorization: Bearer {createdKey.token.slice(0, 12)}…
        </p>
      </div>
    </Card>
  );
}

/** Shows a revealed or rolled key's full token with a copy button. */
function TokenPanel({
  name: _name,
  token,
  title,
  note,
  onClose,
}: {
  name: string;
  token: string;
  title: string;
  note: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Card
      title={title}
      action={
        <Button size="sm" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-warn">{note}</p>
        <div className="flex gap-2">
          <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-sm text-ink">
            {token}
          </code>
          <Button
            icon={<Copy className="size-4" />}
            onClick={async () => {
              await navigator.clipboard.writeText(token);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
